import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Settings2, X } from 'lucide-react';
import { marcarHorarioRevisado, updateAgenda, toMin, WEEKDAY_LABELS } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import MarcaDeLaAgenda from './MarcaDeLaAgenda';
import ProfesionalAgenda from './ProfesionalAgenda';
import AdmiteVisitanteCasilla from './AdmiteVisitanteCasilla';

/**
 * Configuración de una agenda (solo admin): nombre/color, tamaño de hueco,
 * horario semanal y duración por concepto (overrides).
 *
 * «Marca de los mensajes al paciente» (29/09/2026): la marca de Configuración
 * → Marcas con la que se presenta la agenda. Con ella, la confirmación y el
 * recordatorio de sus citas llevan el nombre, la dirección y el teléfono de la
 * marca; sin ella (lo de siempre), los de Configuración. Qué agenda es de qué
 * marca lo decide la clínica aquí, agenda por agenda: no hay ninguna regla
 * escrita en el código (ver MarcaDeLaAgenda).
 *
 * «Profesional» (30/09/2026): el usuario del equipo de la agenda o, si aún no
 * tiene, su nombre. Se cambia aquí cuando le dan usuario; solo viaja si se ha
 * cambiado, y el servidor pasa al nuevo las citas de la agenda que llevaban al
 * anterior (para que pueda abrir las fichas de sus pacientes).
 *
 * «Admite médico visitante» (07/10/2026): si en esta agenda se apunta el
 * médico visitante del día. Solo en las que la clínica marque aquí.
 *
 * «Horario revisado (es correcto así)» (15/10/2026): si la agenda abre de
 * lunes a viernes sin que nadie haya revisado su horario
 * (`horario_por_revisar`, del servidor) y el horario de aquí sigue abriendo
 * los cinco laborables, junto al horario sale este botón, para la agenda que
 * de verdad abre de lunes a viernes. Lo marca en el acto
 * (POST /api/agendas/{id}/horario-revisado) sin guardar lo demás; la lista de
 * fuera se pone al día al cerrar (`onSaved`). Si aquí se cierra algún día de
 * lunes a viernes, al guardar queda revisada (lo decide el servidor, con la
 * misma regla que `abreLosCincoLaborables`).
 *
 * Props:
 *  - open / onOpenChange
 *  - agenda:    agenda a editar
 *  - conceptos: catálogo de conceptos (para la tabla de overrides)
 *  - onSaved:   callback con la agenda actualizada
 */

const SLOT_OPTIONS = [5, 10, 15, 20, 30];

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** ¿Abre este horario de lunes a viernes, cada día con al menos una franja
 *  completa (sean cuales sean las horas)? La misma regla del servidor
 *  (backend/services/horario_agenda.abre_los_cinco_laborables), que es quien
 *  decide; aquí solo sirve para decir qué pasará al guardar. */
const abreLosCincoLaborables = (horario) => [0, 1, 2, 3, 4].every((i) => (
  ((horario || {})[String(i)] || []).some((r) => HHMM.test(r?.start || '') && HHMM.test(r?.end || '')
    && r.end > r.start)
));

export default function AgendaConfigDialog({ open, onOpenChange, agenda, conceptos = [], onSaved }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2E6E5E');
  const [slotMinutes, setSlotMinutes] = useState('15');
  const [descripcion, setDescripcion] = useState('');
  const [wh, setWh] = useState({}); // {"0".."6": [{start, end}]}
  const [overrides, setOverrides] = useState({}); // {code: "min" | ""}
  // Solo quirófanos: límite de procedimientos por turno ('' = sin límite).
  const [maxQxManana, setMaxQxManana] = useState('');
  const [maxQxTarde, setMaxQxTarde] = useState('');
  const [alquilerModulo, setAlquilerModulo] = useState('');
  const [marcaClave, setMarcaClave] = useState(''); // '' = sin marca
  const [marcaBloqueada, setMarcaBloqueada] = useState(false);
  const [profesional, setProfesional] = useState({ userId: '', nombre: '' }); // userId '' = sin usuario
  const [admiteVisitante, setAdmiteVisitante] = useState(false);
  const [saving, setSaving] = useState(false);
  // La agenda que devolvió «Horario revisado» (null si no se ha pulsado).
  const [revisada, setRevisada] = useState(null);
  const [marcandoRevisado, setMarcandoRevisado] = useState(false);

  const esQuirofano = agenda?.tipo === 'quirofano';

  useEffect(() => {
    if (!open || !agenda) return;
    setName(agenda.name || '');
    setColor(agenda.color || '#2E6E5E');
    setSlotMinutes(String(agenda.slot_minutes || 15));
    setDescripcion(agenda.descripcion || '');
    const next = {};
    for (let i = 0; i < 7; i++) {
      next[String(i)] = (agenda.working_hours?.[String(i)] || []).map((r) => ({ ...r }));
    }
    setWh(next);
    const ov = {};
    Object.entries(agenda.concept_overrides || {}).forEach(([code, min]) => {
      ov[code] = String(min);
    });
    setOverrides(ov);
    setMaxQxManana(agenda.max_qx_manana ? String(agenda.max_qx_manana) : '');
    setMaxQxTarde(agenda.max_qx_tarde ? String(agenda.max_qx_tarde) : '');
    setAlquilerModulo(agenda.alquiler_modulo ? String(agenda.alquiler_modulo) : '');
    setMarcaClave(agenda.marca_clave || '');
    setMarcaBloqueada(Boolean(agenda.marca_bloqueada));
    setProfesional({ userId: agenda.doctor_user_id || '', nombre: agenda.doctor_name || '' });
    setAdmiteVisitante(Boolean(agenda.admite_visitante));
    setSaving(false);
    setRevisada(null);
    setMarcandoRevisado(false);
  }, [open, agenda]);

  // Abre de lunes a viernes sin revisar: mientras el horario de aquí siga
  // abriendo los cinco laborables se ofrece «Horario revisado»; si se cierra
  // alguno, al guardar queda revisada (lo decide el servidor).
  const porRevisar = Boolean(agenda?.horario_por_revisar) && !revisada;
  const abreDeLunesAViernes = abreLosCincoLaborables(wh);

  // Cerrar (Cancelar, la equis, fuera): si se ha marcado el horario como
  // revisado, la lista de fuera se pone al día con la agenda que devolvió.
  const cerrar = (abierto) => {
    if (!abierto && revisada) onSaved?.(revisada);
    onOpenChange(abierto);
  };

  const marcarRevisado = async () => {
    if (!agenda?.id) return;
    setMarcandoRevisado(true);
    try {
      const actualizada = await marcarHorarioRevisado(agenda.id);
      setRevisada(actualizada || { ...agenda, horario_revisado: true, horario_por_revisar: false });
      toast.success('Horario marcado como revisado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo marcar el horario como revisado');
    } finally {
      setMarcandoRevisado(false);
    }
  };

  // El profesional solo viaja si ha cambiado: guardar el horario de una agenda
  // no tiene por qué volver a decidir de quién es.
  const profesionalCambiado = profesional.userId !== (agenda?.doctor_user_id || '')
    || (!profesional.userId && profesional.nombre.trim() !== (agenda?.doctor_name || '').trim());

  const addRange = (day) => {
    setWh((prev) => {
      const ranges = prev[day] || [];
      const last = ranges[ranges.length - 1];
      const nextRange = last ? { start: last.end || '16:00', end: '20:00' } : { start: '09:00', end: '14:00' };
      return { ...prev, [day]: [...ranges, nextRange] };
    });
  };

  const removeRange = (day, idx) => {
    setWh((prev) => ({ ...prev, [day]: (prev[day] || []).filter((_, i) => i !== idx) }));
  };

  const setRange = (day, idx, field, value) => {
    setWh((prev) => ({
      ...prev,
      [day]: (prev[day] || []).map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));
  };

  const submit = async () => {
    if (!agenda?.id) return;
    if (!name.trim()) {
      toast.error('El nombre de la agenda es obligatorio');
      return;
    }
    if (profesionalCambiado && !profesional.userId && !profesional.nombre.trim()) {
      toast.error('Falta el profesional: elige su usuario o, si aún no tiene, escribe su nombre');
      return;
    }
    // Validación del horario: todos los rangos completos y coherentes.
    const working_hours = {};
    for (let i = 0; i < 7; i++) {
      const day = String(i);
      const ranges = wh[day] || [];
      for (const r of ranges) {
        if (!r.start || !r.end || toMin(r.end) <= toMin(r.start)) {
          toast.error(`Revisa el horario del ${WEEKDAY_LABELS[i].toLowerCase()}: la hora de fin debe ser posterior a la de inicio`);
          return;
        }
      }
      working_hours[day] = ranges.map((r) => ({ start: r.start, end: r.end }));
    }
    // Overrides: solo los que difieran del default del concepto.
    const concept_overrides = {};
    for (const c of conceptos) {
      const raw = String(overrides[c.code] ?? '').trim();
      if (!raw) continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error(`Duración no válida para "${c.name}"`);
        return;
      }
      if (n !== c.default_minutes) concept_overrides[c.code] = n;
    }
    // Límites de quirófano: enteros >= 0 (vacío = sin límite = null).
    const parseLimite = (raw, etiqueta) => {
      const s = String(raw ?? '').trim();
      if (!s) return null;
      const n = Number(s);
      if (!Number.isInteger(n) || n < 0) {
        toast.error(`Límite de la ${etiqueta} no válido (número entero, 0 = sin límite)`);
        return undefined; // undefined = error de validación
      }
      return n || null; // 0 se guarda como null (sin límite)
    };
    let max_qx_manana = null;
    let max_qx_tarde = null;
    if (esQuirofano) {
      max_qx_manana = parseLimite(maxQxManana, 'mañana');
      if (max_qx_manana === undefined) return;
      max_qx_tarde = parseLimite(maxQxTarde, 'tarde');
      if (max_qx_tarde === undefined) return;
    }
    setSaving(true);
    try {
      const updated = await updateAgenda(agenda.id, {
        name: name.trim(),
        color,
        slot_minutes: Number(slotMinutes),
        descripcion: descripcion.trim(),
        working_hours,
        concept_overrides,
        ...(esQuirofano ? { max_qx_manana, max_qx_tarde } : {}),
        alquiler_modulo: Number(String(alquilerModulo).replace(',', '.')) || null,
        marca_clave: marcaClave,
        marca_bloqueada: marcaBloqueada,
        // Va siempre; el servidor solo lo apunta si cambia.
        admite_visitante: admiteVisitante,
        ...(profesionalCambiado ? {
          doctor_user_id: profesional.userId || null,
          doctor_name: profesional.nombre.trim(),
        } : {}),
      });
      toast.success('Agenda actualizada');
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la agenda');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={cerrar}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="agenda-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-[#13231E]" />
            Configurar agenda
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Nombre + color */}
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label>Nombre</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5"
                data-testid="agenda-config-name"
              />
            </div>
            <div>
              <Label>Color</Label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1.5 block h-10 w-14 rounded-md border border-input bg-white p-1 cursor-pointer"
                aria-label="Color de la agenda"
                data-testid="agenda-config-color"
              />
            </div>
          </div>

          {/* Profesional (30/09/2026): para cuando le den usuario */}
          <ProfesionalAgenda
            prefijo="agenda-config"
            userId={profesional.userId}
            nombre={profesional.nombre}
            onChange={setProfesional}
            ayuda={profesionalCambiado
              ? 'Al guardar, las citas de esta agenda que llevaban al profesional anterior pasan al nuevo: así puede abrir las fichas de sus pacientes.'
              : 'Quien ve esta agenda como suya. Si aún no tiene usuario, su nombre; cuando lo tenga, elígelo aquí.'}
          />

          {/* Qué se hace aquí — lo lee el Agente */}
          <div>
            <Label>Qué se hace en esta agenda</Label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder="Ej.: lifting facial, deep plane, cuello, mentoplastia, lipofilling facial"
              className="mt-1.5 w-full rounded-md border border-input bg-white p-2 text-sm"
              data-testid="agenda-config-descripcion"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Los procedimientos que se atienden aquí, separados por comas. <strong>El Agente
              lo usa para saber a qué profesional mandar cada paciente</strong>: si lo dejas
              vacío, tendrá que preguntar en vez de proponer.
            </p>
          </div>

          {/* Médico visitante del día (07/10/2026): solo en las marcadas. */}
          <AdmiteVisitanteCasilla
            prefijo="agenda-config"
            checked={admiteVisitante}
            onChange={setAdmiteVisitante}
          />

          {/* Marca de los mensajes al paciente (29/09/2026) */}
          <MarcaDeLaAgenda
            prefijo="agenda-config"
            marcaClave={marcaClave}
            onMarcaClave={setMarcaClave}
            marcaBloqueada={marcaBloqueada}
            onMarcaBloqueada={setMarcaBloqueada}
          />

          {/* Tamaño de hueco */}
          <div>
            <Label>Tamaño de hueco</Label>
            <Select value={slotMinutes} onValueChange={setSlotMinutes}>
              <SelectTrigger className="mt-1.5 w-40" data-testid="agenda-config-slot">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SLOT_OPTIONS.map((m) => (
                  <SelectItem key={m} value={String(m)}>
                    {m} min
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Las citas empiezan siempre en múltiplos de este valor.</p>
          </div>

          {/* Solo quirófanos: límite de procedimientos por turno */}
          {esQuirofano && (
            <div data-testid="agenda-config-limites-qx">
              <Label>Límite de procedimientos por turno</Label>
              <div className="grid grid-cols-2 gap-3 mt-1.5">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Mañana (hasta 15:00)</div>
                  <Input
                    type="number"
                    min="0"
                    value={maxQxManana}
                    onChange={(e) => setMaxQxManana(e.target.value)}
                    placeholder="Sin límite"
                    data-testid="agenda-config-max-manana"
                  />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Tarde (desde 15:00)</div>
                  <Input
                    type="number"
                    min="0"
                    value={maxQxTarde}
                    onChange={(e) => setMaxQxTarde(e.target.value)}
                    placeholder="Sin límite"
                    data-testid="agenda-config-max-tarde"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Al llegar al límite, ese turno deja de ofrecer huecos (el admin puede forzar).
                Vacío o 0 = sin límite.
              </p>
            </div>
          )}

          {/* Alquiler de módulo a un externo (2026-07-28). Solo deja
              constancia de los módulos usados: no cobra ni toca la caja. */}
          <div data-testid="agenda-config-alquiler">
            <Label>Alquiler de módulo (externos)</Label>
            <Input
              type="text"
              inputMode="decimal"
              className="mt-1.5"
              value={alquilerModulo}
              onChange={(e) => setAlquilerModulo(e.target.value)}
              placeholder="Vacío = no alquila"
              data-testid="agenda-config-alquiler-modulo"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Lo que se le cobra por CADA módulo usado. Mañana y tarde cuentan
              por separado (corte a las 15:00). Los módulos usados y su total
              salen en Honorarios; aquí no se cobra nada.
            </p>
          </div>

          <Separator />

          {/* Horario semanal */}
          <div>
            <Label>Horario semanal</Label>
            {/* Abre de lunes a viernes sin revisar (15/10/2026). */}
            {porRevisar && abreDeLunesAViernes && (
              <div
                className="mt-1.5 flex flex-wrap items-center gap-2 rounded-md border border-amber-300/80 bg-amber-50 px-2.5 py-1.5 text-xs text-[#13231E] dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-50"
                data-testid="agenda-config-horario-sin-revisar"
              >
                <span className="flex-1 min-w-[12rem]">
                  Esta agenda abre de lunes a viernes y nadie ha revisado su horario: la vista «Mes» la da
                  como abierta todos los días laborables. Ciérrale aquí los días que no pasa consulta o, si
                  es correcto así, márcalo como revisado.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0 bg-white dark:bg-transparent"
                  onClick={marcarRevisado}
                  disabled={marcandoRevisado}
                  data-testid="agenda-config-horario-revisado"
                >
                  {marcandoRevisado && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                  Horario revisado (es correcto así)
                </Button>
              </div>
            )}
            {porRevisar && !abreDeLunesAViernes && (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="agenda-config-horario-quedara-revisado">
                Al guardar, este horario queda revisado: ya no abre todos los días de lunes a viernes.
              </p>
            )}
            {revisada && (
              <p className="mt-1 text-xs text-[#2E6E5E] dark:text-primary" data-testid="agenda-config-horario-marcado">
                Horario marcado como revisado.
              </p>
            )}
            <div className="mt-2 space-y-1.5">
              {WEEKDAY_LABELS.map((label, i) => {
                const day = String(i);
                const ranges = wh[day] || [];
                return (
                  <div key={day} className="flex items-start gap-3 py-1" data-testid={`agenda-config-day-${day}`}>
                    <div className="w-24 shrink-0 pt-1.5 text-sm font-medium text-[#13231E]">{label}</div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      {ranges.length === 0 && (
                        <div className="text-sm text-muted-foreground/70 pt-1.5">Cerrado</div>
                      )}
                      {ranges.map((r, idx) => (
                        <div key={idx} className="flex items-center gap-1.5">
                          <Input
                            type="time"
                            value={r.start || ''}
                            onChange={(e) => setRange(day, idx, 'start', e.target.value)}
                            className="w-28 tabular-nums"
                            data-testid={`agenda-config-day-${day}-start-${idx}`}
                          />
                          <span className="text-muted-foreground text-sm">–</span>
                          <Input
                            type="time"
                            value={r.end || ''}
                            onChange={(e) => setRange(day, idx, 'end', e.target.value)}
                            className="w-28 tabular-nums"
                            data-testid={`agenda-config-day-${day}-end-${idx}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-red-700"
                            onClick={() => removeRange(day, idx)}
                            aria-label={`Quitar rango del ${label.toLowerCase()}`}
                            data-testid={`agenda-config-day-${day}-remove-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 mt-0.5 text-[#2E6E5E] hover:text-[#13231E]"
                      onClick={() => addRange(day)}
                      aria-label={`Añadir rango al ${label.toLowerCase()}`}
                      data-testid={`agenda-config-day-${day}-add`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Overrides de duración por concepto */}
          <div>
            <Label>Duración por concepto (min)</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Deja el campo vacío para usar la duración por defecto del concepto.
            </p>
            <div className="rounded-md border border-[#D9E3DD] divide-y divide-[#F0F6F2]">
              {conceptos.map((c) => (
                <div key={c.code} className="flex items-center gap-3 px-3 py-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: c.color || '#2E6E5E' }}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0 text-sm text-[#13231E] truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground shrink-0">por defecto {c.default_minutes} min</div>
                  <Input
                    type="number"
                    min={5}
                    step={5}
                    value={overrides[c.code] ?? ''}
                    onChange={(e) => setOverrides((prev) => ({ ...prev, [c.code]: e.target.value }))}
                    placeholder={String(c.default_minutes)}
                    className="w-20 shrink-0 tabular-nums"
                    data-testid={`agenda-config-override-${c.code}`}
                  />
                </div>
              ))}
              {conceptos.length === 0 && (
                <div className="px-3 py-4 text-sm text-muted-foreground">No hay conceptos de cita definidos.</div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => cerrar(false)} data-testid="agenda-config-cancel">
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="agenda-config-save"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
